import { useState } from 'react';
import { localised, useI18n } from '../i18n';
import { percent } from '../Progress';
import type { Objective, ProgressView } from '../api';
import Sheet from './Sheet';

/**
 * Two words and a number, and everything else a tap away.
 *
 * The specification asks for "a small header with the objective short name only followed by
 * the % of progress", and *only* is the load-bearing word. This is the top of the screen a
 * learner spends nearly all their time on; every line it takes is a line of conversation
 * they do not see. So it is one row, it never wraps, and the long name truncates rather
 * than pushing the thread down.
 *
 * The two things it hides are the two things that would otherwise have to live on another
 * screen: what they actually asked for, in their own words, and where they have got to.
 * Both are one tap from the thread and neither navigates away from it, which is the whole
 * argument for the sheet — see `Sheet.tsx`.
 *
 * **Both are buttons rather than a div with a click handler.** They are interactive, they
 * take focus, they announce their state, and a phone's accessibility tooling has to be able
 * to find them. `aria-expanded` is what tells a screen reader that the tap reveals
 * something rather than going somewhere.
 */

/** The percentage, or a dash. Never a spinner. */
function Figure({ progress }: { progress: ProgressView | null | undefined }) {
  /*
   * A dash while it loads, and a dash when there is nothing to measure.
   *
   * Deliberately the same character for both, which is not laziness: the difference matters
   * for a second and a half and the alternative is a header that flickers through a
   * skeleton, a spinner and a number every time the screen mounts — movement at the top of
   * a thread somebody is reading. The distinction is not lost, it is moved: tapping says
   * which of the two it is, in a sentence, where there is room to say it properly.
   */
  if (!progress || progress.mastery === null) return <span aria-hidden="true">—</span>;
  return <>{percent(progress.mastery)}</>;
}

/** One count, with the word that says what it counts. Never a bare number. */
function Count({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="chat-figure">
      <span className="chat-figure-n">{value}</span>
      <span className="chat-figure-label">{label}</span>
    </div>
  );
}

export default function Header({
  objective,
  progress,
}: {
  /** `undefined` while it is being read, `null` when nothing is running. */
  objective: Objective | null | undefined;
  progress: ProgressView | null | undefined;
}) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState<'asked' | 'progress' | undefined>();
  const close = () => setOpen(undefined);

  const name = objective ? localised(objective.title, locale, t('chat.head.untitled')) : '';
  const overall = progress?.overall;

  return (
    <header className="chat-head">
      <button
        type="button"
        className="chat-head-name"
        onClick={() => setOpen(open === 'asked' ? undefined : 'asked')}
        aria-expanded={open === 'asked'}
      >
        {/* The placeholder is a word rather than an empty element, so the header keeps its
            height from the first frame and the thread below it does not jump down by a row
            once the objective arrives. */}
        <span className="chat-head-title">{name || t('chat.head.loading')}</span>
        <span className="chat-head-caret" aria-hidden="true">
          ›
        </span>
      </button>

      <button
        type="button"
        className="chat-head-figure"
        onClick={() => setOpen(open === 'progress' ? undefined : 'progress')}
        aria-expanded={open === 'progress'}
        aria-label={t('chat.head.tapProgress')}
      >
        <Figure progress={progress} />
      </button>

      {open === 'asked' && (
        <Sheet title={t('chat.head.asked')} onClose={close}>
          {objective ? (
            <>
              {/* Verbatim, and with their line breaks kept. This is the text they typed and
                  the text the research was run against — paraphrasing it here would make
                  the app disagree with itself about what it was asked to teach. */}
              <p className="chat-asked">{objective.description}</p>
              <p className="chat-sheet-note">
                {objective.target.mode === 'date'
                  ? `${t('chat.head.byDate')} ${new Date(objective.target.targetDate).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })}`
                  : `${t('chat.head.toHold')} ${percent(objective.target.targetScore)}`}
              </p>
            </>
          ) : (
            <p className="chat-sheet-note">{t('chat.head.noObjective')}</p>
          )}
        </Sheet>
      )}

      {open === 'progress' && (
        <Sheet title={t('chat.head.progress')} onClose={close}>
          {progress && overall ? (
            <>
              <p className="chat-sheet-lede">
                {progress.mastery === null
                  ? t('chat.prog.nothing')
                  : `${percent(progress.mastery)} · ${t('chat.prog.mastered')}`}
              </p>
              {/*
               * The three counts, never merged, and never shown without each other.
               *
               * `Progress.tsx` argues this at length and the argument holds harder in a
               * sheet this small: "not asked yet" and "got it wrong" are the only two things
               * here a learner can respond to differently — one wants time and the other
               * wants attention — so a single "not yet mastered" bar would throw away the
               * only actionable thing on the panel. The denominator is on the row beneath
               * for the same reason: a percentage of an invisible total is the flattering
               * reading rather than the true one.
               */}
              <div className="chat-figures">
                <Count label={t('chat.prog.holding')} value={overall.holding} />
                <Count label={t('chat.prog.struggling')} value={overall.struggling} />
                <Count label={t('chat.prog.untested')} value={overall.untested} />
              </div>
              <p className="chat-sheet-note">
                {t('chat.prog.of').replace('{n}', String(overall.questions))}
              </p>
              <div className="chat-figures">
                <Count label={t('chat.prog.due')} value={overall.due} />
                <Count label={t('chat.prog.today')} value={progress.load.answeredToday} />
              </div>
              {progress.load.paused && <p className="chat-sheet-note">{t('chat.prog.paused')}</p>}
              {/* A count that is a floor rather than a total has to say so, or the sheet is
                  quietly wrong about a curriculum big enough to be worth being right about. */}
              {progress.partial && <p className="chat-sheet-note">{t('chat.prog.partial')}</p>}
            </>
          ) : (
            <p className="chat-sheet-note">{t('chat.prog.none')}</p>
          )}
        </Sheet>
      )}
    </header>
  );
}
